import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Crown, Sparkles, Rocket, Star } from "lucide-react";

const PAID_PRESET = {
  starter:  { label: "Starter",  icon: Rocket, color: "cyan",   subKey: "tierBadge.starter.sub" },
  pro:      { label: "Pro",      icon: Star,   color: "amber",  subKey: "tierBadge.pro.sub" },
  pro_plus: { label: "Pro+",     icon: Crown,  color: "violet", subKey: "tierBadge.proPlus.sub" },
  premium:  { label: "Premium",  icon: Crown,  color: "amber",  subKey: "tierBadge.pro.sub" },
};

const COLOR_CLASSES = {
  cyan:   { bg: "from-cyan-500/10 to-cyan-500/5",     border: "border-cyan-500/40",   icon: "text-cyan-300",   title: "text-cyan-200",   subt: "text-cyan-200/70" },
  amber:  { bg: "from-amber-500/10 to-amber-500/5",   border: "border-amber-500/40",  icon: "text-amber-400",  title: "text-amber-300",  subt: "text-amber-200/70" },
  violet: { bg: "from-violet-500/10 to-violet-500/5", border: "border-violet-500/40", icon: "text-violet-400", title: "text-violet-300", subt: "text-violet-200/70" },
};

const DEFAULT_SUB = {
  starter:  "Essential features unlocked",
  pro:      "AI Copilot included",
  pro_plus: "Everything unlocked + Lost Opportunity",
  premium:  "Everything unlocked",
};

export default function TierBadge({ tier, usage, limits, loading = false }) {
  const { t } = useTranslation();
  // Fixed-size placeholder until the real plan is known (no free→paid swap).
  if (loading) return <div className="h-[50px] rounded-md bg-slate-900 border border-slate-800" data-testid="tier-badge-loading"/>;
  const paid = PAID_PRESET[tier];
  if (paid) {
    const Icon = paid.icon;
    const c = COLOR_CLASSES[paid.color];
    const sub = t(paid.subKey, { defaultValue: DEFAULT_SUB[tier] || "" });
    return (
      <Link
        to="/billing"
        className={`block px-3 py-2 rounded-md bg-gradient-to-br ${c.bg} border ${c.border} hover:brightness-125 transition-all flex items-center gap-2`}
        data-testid={`tier-badge-${tier}`}
      >
        <Icon size={14} className={c.icon}/>
        <div className="flex-1 min-w-0">
          <div className={`text-xs font-display font-semibold ${c.title}`}>{t("tierBadge.plan", { defaultValue: "Plan" })} {paid.label}</div>
          <div className={`text-[10px] ${c.subt} truncate`}>{sub}</div>
        </div>
      </Link>
    );
  }

  const quotesLeft = Math.max(0, (limits?.quotes_per_month ?? 10) - (usage?.quotes_this_month ?? 0));
  const progress = Math.min(100, ((usage?.quotes_this_month ?? 0) / (limits?.quotes_per_month ?? 10)) * 100);
  return (
    <Link to="/billing" className="block px-3 py-2 rounded-md bg-slate-900 border border-slate-800 hover:border-blue-500/60 transition-colors" data-testid="tier-badge-free">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] label-tech text-slate-500">{t("tierBadge.planFree", { defaultValue: "Plan FREE" })}</div>
        <div className="flex items-center gap-1 text-[10px] text-blue-400">
          <Sparkles size={10}/> {t("tierBadge.upgrade", { defaultValue: "Upgrade" })}
        </div>
      </div>
      <div className="text-xs text-slate-300 mb-1.5 font-mono-data">{t("tierBadge.quotesLeft", { n: quotesLeft, defaultValue: `${quotesLeft} quotes left` })}</div>
      <div className="h-1 rounded-full bg-slate-800 overflow-hidden">
        <div className="h-full bg-blue-500" style={{ width: `${progress}%` }}/>
      </div>
    </Link>
  );
}
